import {
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import type { CarrierOffer } from "@voice/api-contract";

type Api = <T>(path: string, options?: { signal?: AbortSignal }) => Promise<T>;
const base = "/admin/carriers/twilio";
const time = (value: string) => new Date(value).toLocaleString();

function ResultTable({
  label,
  headings,
  children,
}: {
  label: string;
  headings: string[];
  children: ReactNode;
}) {
  return (
    <div
      className="cr-table-wrap"
      role="region"
      aria-label={label}
      tabIndex={0}
    >
      <table>
        <caption className="cr-sr-only">{label}</caption>
        <thead>
          <tr>
            {headings.map((title) => (
              <th scope="col" key={title}>
                {title}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

export function NumberSearch({
  api,
  address,
  onSelect,
}: {
  api: Api;
  address?: { city: string; state: string };
  onSelect?: (number: string) => void;
}) {
  const [mode, setMode] = useState(address ? "address" : "area");
  const [result, setResult] = useState<{
    items: CarrierOffer[];
    checkedAt: string;
  } | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const pending = useRef<AbortController | null>(null);
  useEffect(() => () => pending.current?.abort(), []);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    pending.current?.abort();
    const controller = new AbortController();
    pending.current = controller;
    const form = new FormData(event.currentTarget);
    const query =
      mode === "address" && address
        ? new URLSearchParams({ city: address.city, state: address.state })
        : mode === "city"
          ? new URLSearchParams({
              city: String(form.get("city")),
              state: String(form.get("state")),
            })
          : new URLSearchParams({ areaCode: String(form.get("areaCode")) });
    setBusy(true);
    setResult(null);
    setError("");
    try {
      const value = await api<{ items: CarrierOffer[]; checkedAt: string }>(
        `${base}/search?${query}`,
        { signal: controller.signal },
      );
      if (!controller.signal.aborted) setResult(value);
    } catch (cause) {
      if (!controller.signal.aborted) setError((cause as Error).message);
    } finally {
      if (!controller.signal.aborted) setBusy(false);
    }
  }
  const price = (amount: string | null, currency: string | null) =>
    amount !== null && currency ? `${currency} ${amount}` : "Not quoted";
  return (
    <section className="cr-panel">
      <h2>Find a new number</h2>
      <p>
        Live Twilio availability, up to 20 results. Searching does not reserve
        or buy a number.
      </p>
      <form className="cr-form" onSubmit={submit}>
        <label className="cr-field">
          <span>Where should the number be local?</span>
          <select
            value={mode}
            onChange={(event) => {
              setMode(event.target.value);
              setResult(null);
              setError("");
              pending.current?.abort();
              setBusy(false);
            }}
          >
            {address && (
              <option value="address">
                Customer's address: {address.city}, {address.state}
              </option>
            )}
            <option value="area">Search an area code</option>
            <option value="city">Search another city</option>
          </select>
        </label>
        {mode === "address" && address && (
          <p>
            Uses the city and state from the service address. Results depend on
            carrier availability.
          </p>
        )}
        {mode === "city" && (
          <div className="cr-form-grid">
            <label className="cr-field">
              <span>City</span>
              <input
                name="city"
                required
                maxLength={100}
                autoComplete="address-level2"
              />
            </label>
            <label className="cr-field">
              <span>State</span>
              <input
                name="state"
                required
                minLength={2}
                maxLength={2}
                pattern="[A-Za-z]{2}"
                autoComplete="address-level1"
                placeholder="Two-letter abbreviation"
              />
            </label>
          </div>
        )}
        {mode === "area" && (
          <>
            <label className="cr-field">
              <span>Area code</span>
              <input
                name="areaCode"
                type="text"
                inputMode="numeric"
                pattern="[2-9][0-9]{2}"
                minLength={3}
                maxLength={3}
                required
                autoComplete="off"
                aria-describedby="carrier-search-help"
              />
            </label>
            <p id="carrier-search-help">Enter a three-digit US area code.</p>
          </>
        )}
        <button className="cr-button" disabled={busy}>
          {busy ? "Searching Twilio…" : "Search numbers"}
        </button>
      </form>
      {busy && <p role="status">Checking current number availability…</p>}
      {error && (
        <p className="cr-notice error" role="alert">
          {error} Submit the search again to retry.
        </p>
      )}
      {result && (
        <>
          <p>
            Checked {time(result.checkedAt)}. Carrier costs below are not Cache
            River customer prices. Taxes and other charges are not included.
          </p>
          {result.items.length ? (
            <ResultTable
              label="Available Twilio numbers"
              headings={[
                "Telephone number",
                "Region",
                "Features",
                "Carrier setup cost",
                "Carrier monthly cost",
                ...(onSelect ? ["Preference"] : []),
              ]}
            >
              {result.items.map((row) => (
                <tr key={row.number}>
                  <td>{row.number}</td>
                  <td>{row.region || "Not supplied"}</td>
                  <td>{row.features.join(", ") || "Not supplied"}</td>
                  <td>{price(row.upfront, row.currency)}</td>
                  <td>{price(row.monthly, row.currency)}</td>
                  {onSelect && (
                    <td>
                      <button
                        type="button"
                        className="cr-button secondary"
                        onClick={() => onSelect(row.number)}
                      >
                        Choose {row.number}
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </ResultTable>
          ) : (
            <p>
              No matching numbers available. Search another city or area code.
            </p>
          )}
          <p>
            {onSelect
              ? "Choosing saves a preference for this customer. It does not reserve, purchase or activate the number."
              : "Purchasing from this portal is not enabled yet. These results are not held."}
          </p>
        </>
      )}
    </section>
  );
}

export function CarriersPanel({ api }: { api: Api }) {
  return <TwilioConnections api={api} />;
}

type TwilioReadStatus =
  | { state: "connected"; checkedAt: string; access: "read_only" }
  | { state: "error" | "not_configured"; detail: string };
type TwilioAccountStatus = { id: string; label: string } & TwilioReadStatus;

export function TwilioConnectionResults({
  accounts,
  error,
  onRefresh,
}: {
  accounts: TwilioAccountStatus[] | null;
  error: string;
  onRefresh: () => void;
}) {
  return (
    <section className="cr-panel">
      <h2>Twilio</h2>
      {!accounts && !error && <p role="status">Checking Twilio accounts…</p>}
      {error && (
        <p className="cr-notice error" role="alert">
          {error} Use Check Twilio accounts to retry.
        </p>
      )}
      {accounts?.length === 0 && (
        <p>
          No Twilio accounts connected. A staff administrator can configure
          inventory access on the portal server.
        </p>
      )}
      {accounts?.map((account) => (
        <p
          key={account.id}
          role={account.state === "error" ? "alert" : "status"}
          className={account.state === "error" ? "cr-notice error" : undefined}
        >
          {account.label}:{" "}
          {account.state === "connected"
            ? `Connected for read-only inventory. Verified ${time(account.checkedAt)}. Owned numbers are on the Numbers page.`
            : account.detail}
        </p>
      ))}
      <p>
        Existing-number assignments link an owned number to a portal customer.
        They do not purchase numbers, submit ports, or change live call routing.
        Pending-port records remain separate from completed service.
      </p>
      <button
        className="cr-button secondary"
        disabled={!accounts && !error}
        onClick={onRefresh}
      >
        Check Twilio accounts
      </button>
    </section>
  );
}

function TwilioConnections({ api }: { api: Api }) {
  const [accounts, setAccounts] = useState<TwilioAccountStatus[] | null>(null);
  const [error, setError] = useState("");
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    const abort = new AbortController();
    setAccounts(null);
    setError("");
    void (async () => {
      try {
        const configured = await api<{
          items: { id: string; label: string }[];
        }>("/admin/carriers/twilio/accounts", { signal: abort.signal });
        const result = await Promise.all(
          configured.items.map(
            async (account): Promise<TwilioAccountStatus> => {
              try {
                const status = await api<TwilioReadStatus>(
                  `/admin/carriers/twilio/status?account=${encodeURIComponent(account.id)}`,
                  { signal: abort.signal },
                );
                return { ...account, ...status };
              } catch (cause) {
                return {
                  ...account,
                  state: "error",
                  detail: `${(cause as Error).message} Check Twilio accounts to retry.`,
                };
              }
            },
          ),
        );
        if (!abort.signal.aborted) setAccounts(result);
      } catch (cause) {
        if (!abort.signal.aborted) setError((cause as Error).message);
      }
    })();
    return () => abort.abort();
  }, [api, revision]);
  return (
    <TwilioConnectionResults
      accounts={accounts}
      error={error}
      onRefresh={() => setRevision((value) => value + 1)}
    />
  );
}
